// v2.5.186 — useWakeLock
// ----------------------------------------------------------------------
// Keeps the device screen awake while a page that needs it is mounted —
// e.g. the scoreboard during a game, where the coach sets the phone on
// the bench and doesn't want it locking every 30s.
//
// Uses the standard Screen Wake Lock API (navigator.wakeLock). Notes:
//   • Only works over HTTPS (we are) and after a user gesture in some
//     browsers — mounting after a tap (navigating to the page) satisfies
//     this in practice.
//   • The lock is automatically released by the browser when the tab is
//     hidden/backgrounded, so we re-acquire on visibilitychange when the
//     page becomes visible again.
//   • Gracefully no-ops where unsupported (older iOS Safari < 16.4).
//     iOS 16.4+ and modern Android Chrome support it. Where unsupported,
//     the screen simply behaves normally — no error, no crash.
//
// Usage:
//   useWakeLock(true);            // hold a wake lock while mounted
//   useWakeLock(isLiveGame);      // conditional
import { useEffect, useRef } from 'react';

export function useWakeLock(active = true) {
  const lockRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
      return; // unsupported — no-op
    }

    let released = false;

    const acquire = async () => {
      try {
        // Only acquire when the page is actually visible; requesting
        // while hidden throws.
        if (document.visibilityState !== 'visible') return;
        lockRef.current = await navigator.wakeLock.request('screen');
        // If the browser drops the lock on its own, clear our ref so the
        // visibility handler knows to re-acquire.
        lockRef.current.addEventListener?.('release', () => {
          lockRef.current = null;
        });
      } catch {
        // Permission denied / not allowed in this context — ignore.
        lockRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !lockRef.current && !released) {
        acquire();
      }
    };

    acquire();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      try { lockRef.current?.release?.(); } catch { /* ignore */ }
      lockRef.current = null;
    };
  }, [active]);
}

export default useWakeLock;
